import React from "react";
import {
  Typography,
  Container,
  Paper,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
  Divider
} from "@mui/material";
import { Refresh, CheckCircle, Cancel, DoneAll, ShoppingBag } from "@mui/icons-material";

import Client from "./components/Client";
import Config from "./components/ClientConfig";

const AUTO_REFRESH_MS = 600000;

function StatusChip({ status }) {
  const map = {
    5: { label: "Paid", color: "warning" },
    6: { label: "Accepted", color: "info" },
    // 7: { label: "Ready", color: "success" },
  };
  const m = map[status] || { label: status, color: "default" };
  return (
    <Chip
      size="small"
      label={m.label}
      color={m.color}
      variant={m.color === "default" ? "outlined" : "filled"}
    />
  );
}

function Actions({ row, onAccept, onCancel, onComplete, onDetails }) {
  const st = Number(row.status); // 5 = Paid, 6 = Accepted, 7 = Ready

  const showAccept = st === 5;   // Paid -> Accept
  const showCancel = st === 5;   // Paid -> Cancel
  const showReady  = st === 6;   // Accepted -> Ready
  // Ready (7) -> Details only

  return (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Details">
        <span>
          <Button variant="outlined" size="small" onClick={() => onDetails(row)}>
            Details
          </Button>
        </span>
      </Tooltip>

      {showAccept && (
        <Tooltip title="Accept">
          <span>
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => onAccept(row)}
            >
              Accept
            </Button>
          </span>
        </Tooltip>
      )}

      {showCancel && (
        <Tooltip title="Cancel">
          <span>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Cancel />}
              onClick={() => onCancel(row)}
            >
              Cancel
            </Button>
          </span>
        </Tooltip>
      )}

      {showReady && (
        <Tooltip title="Mark ready">
          <span>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<DoneAll />}
              onClick={() => onComplete(row)}
            >
              Ready
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

export default class LocationOrdersPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      catalogsLoaded: false,
      // data
      orders: [],
      loading: false,
      error: "",
      // ui
      snack: { open: false, msg: "", sev: "success" },
      selectedStatus: 5, // 5 Paid | 6 Accepted | 7 Ready
      selectedDate: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
      detailsDlg: { open: false, order: null },
      cancelDlg: { open: false, order: null, reason: "" },
      // dictionaries
      productNameById: {},   // { iref: name }
      comboNameById: {},     // { iref: name }
      // views
      viewMode: "orders", // "orders" | "returns"
      // returns
      returnDlg: {
        open: false,
        step: 0,            // 0=Find Order, 1=Select Lines
        searchIref: "",
        loaded: false,
        sourceOrder: null,  // raw order snapshot loaded via reports / retrieve
        lines: [],          // flattened selectable lines from source order
        select: {},         // { key: qtyToReturn } (positive numbers; will send as negative)
        busy: false,
        err: "",
        payType: 1, // 1 Cash, 2 Bank Transfer
      },
    }
  }

  // ---------- Small helpers ----------

  parseDate = (s) => {
    if (!s) return 0;
    const [d, t] = String(s).split(" ");
    if (!d) return 0;
    const parts = d.split("-");
    let y, m, day;
    if (parts[0] && parts[0].length === 4) [y, m, day] = parts.map(Number);
    else [day, m, y] = parts.map(Number);
    const [hh = 0, mm = 0, ss = 0] = (t || "00:00:00").split(":").map(Number);
    return new Date(y, m - 1, day, hh, mm, ss).getTime();
  };

  // Initial dictionary fill (may be partial; we'll also resolve missing ones lazily)
  loadDictionaries = async () => {
    const { client } = this.state;

    // TIP: if some names still show as IDs, remove the status filter so you fetch ALL items.
    const pr = await Promise.resolve(client.getData(
      Config.API_URL_PRODUCT + 'list_products/',
      { /* status: 2, */ orderBy_name: true }
    )) || {};
    const pList = Array.isArray(pr) ? pr : pr.products || pr.results || pr.items || [];
    const productNameById = {};
    pList.forEach((p) => {
      const id = String(p?.iref ?? p?.id ?? "");
      if (id) productNameById[id] = p?.name || p?.label || id;
    });

    const cr = await Promise.resolve(client.getData(
      Config.API_URL_COMBO + 'list_combos/',
      { /* status: 2, */ orderBy_name: true }
    )) || {};
    const cList = Array.isArray(cr)
      ? cr
      : (Array.isArray(cr.productCombos) && cr.productCombos.length ? cr.productCombos
         : (Array.isArray(cr.combos) && cr.combos.length ? cr.combos
            : (cr.results || cr.items || [])));

    const comboNameById = {};
    cList.forEach((c) => {
      const id = String(c?.iref ?? c?.id ?? "");
      if (id) comboNameById[id] = c?.name || c?.label || id;
    });

    this.setState({ productNameById, comboNameById, catalogsLoaded: true });
  };

  // ----------------- Data -----------------
  loadOrders = async () => {
    const { client, selectedStatus, selectedDate } = this.state;
    this.setState({ error: "" });
    try {
      const resp = await Promise.resolve(
        client.getData(Config.API_URL_ORDERS, { status: selectedStatus, promisedDate: selectedDate })
      ) || {};
      const list = Array.isArray(resp) ? resp : resp.orders || resp.results || resp.items || [];

      const mapped = list
        .map(this.mapOrderRow)
        .sort((a, b) => this.parseDate(b.created) - this.parseDate(a.created));

      this.setState({ orders: mapped });
    } catch (e) {
      this.setState({ error: e?.message || "Failed to load orders" });
    }
  };

  getProductIref = (p) => String(p?.ProductPrice?.product ?? p?.product ?? "");
  getComboIref   = (c) => String(c?.ComboPrice?.productCombo ?? c?.productCombo ?? "");
  
  getPerson = (o) => o?.client?.user?.person || null;

  getClientName = (o) => {
    const user = o?.client?.user || {};
    const person = user?.person || {};

    const firstName = person.firstName || user.first_name || "";
    const lastName = person.lastName || user.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.email || "-";
  };

  getClientPhone = (o) => {
    const person = this.getPerson(o);
    return person?.phone || o?.OD_order?.[0]?.clientPhone || "-";
  };

  getDeliveryInfo = (o) => {
    const delivery = Array.isArray(o?.OD_order) && o.OD_order.length > 0
      ? o.OD_order[0]
      : null;

    if (!delivery) {
      return { delivery: null, address: null };
    }

    const person = this.getPerson(o);
    const deliveryAddressIref = String(delivery.address || "");

    const additionalAddress = (person?.additionaladdress_person || []).find(
      (x) => String(x?.address?.iref || "") === deliveryAddressIref
    );

    const address =
      additionalAddress?.address ||
      person?.address ||
      null;

    return { delivery, address };
  };

  getDeliveryTypeLabel = (type) => {
    const map = {
      0: "PickUp",
      1: "Delivery",
      2: "Transport",
      3: "Glovo",
      4: "Tazz",
      5: "Bolt",
      6: "Bringo",
      7: "Food Panda",
      8: "Takeaway",
      9: "Undefined",
    };
    return map[Number(type)] || "Undefined";
  };

  getPickupCode = (o, delivery) => {
    const existing = String(delivery?.pickupCode || "").trim();
    if (existing && existing !== "0") {
      return existing;
    }
    const code = String(o?.code || "").trim();
    return code ? code.slice(-4) : "-";
  };

  formatAddress = (address) => {
    if (!address) return "-";

    const city =
      typeof address.city === "object"
        ? address.city?.name
        : address.city || "";

    const parts = [
      address.name,
      [address.street, address.number].filter(Boolean).join(" "),
      address.entrance ? `Entrance: ${address.entrance}` : "",
      address.floor ? `Floor: ${address.floor}` : "",
      address.local ? `Apartment: ${address.local}` : "",
      address.complement,
      city,
      address.postalCode ? `Postal code: ${address.postalCode}` : "",
    ].filter(Boolean);

    return parts.length ? parts.join(", ") : "-";
  };

  InfoLine = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  );
  
  mapOrderRow = (o) => {
    const prodQty = (o.orderProductEntry_order || []).reduce((s, p) => s + Number(p.quantity || 0), 0);
    const comboQty = (o.orderComboEntry_order || []).reduce((s, c) => s + Number(c.quantity || 0), 0);
    const promoProdQty = (o.orderProductPromotionEntry_order || []).reduce((s, pp) => s + Number(pp.quantity || 0), 0);
    const promoComboQty = (o.orderComboPromotionEntry_order || []).reduce((s, cp) => s + Number(cp.quantity || 0), 0);

    const items = [];
    if (prodQty > 0)  items.push({ key: `agg_products_${o.iref}`, label: `product × ${prodQty}`, color: "default" });
    if (comboQty > 0) items.push({ key: `agg_combos_${o.iref}`,   label: `combo × ${comboQty}`,   color: "info" });
    if (promoProdQty > 0)  items.push({ key: `agg_promo_prod_${o.iref}`,  label: `product promo × ${promoProdQty}`, color: "warning" });
    if (promoComboQty > 0) items.push({ key: `agg_promo_combo_${o.iref}`, label: `combo promo × ${promoComboQty}`,  color: "warning" });

    return {
      iref: o.iref,
      number: o.docNumber || o.iref,
      created: o.createdOn,
      status: o.status,
      notes: o.description,
      customerName: o?.client?.user?.first_name
        ? `${o.client.user.first_name} ${o.client.user.last_name}`
        : o?.client?.user?.email || "-",
      locationName: o?.pos?.name || "-",
      items,
      itemsCount:
        (o.orderProductEntry_order?.length || 0) +
        (o.orderComboEntry_order?.length || 0) +
        (o.orderProductPromotionEntry_order?.length || 0) +
        (o.orderComboPromotionEntry_order?.length || 0),
      total: Number(o.totalAmount ?? 0),
      raw: o,
    };
  };

  // ----------------- UI Actions -----------------
  handleStatusClick = (st) => this.setState({ selectedStatus: st }, this.loadOrders);
  handleDateChange = (e) => this.setState({ selectedDate: e.target.value }, this.loadOrders);
  toast = (msg, sev = "success") => this.setState({ snack: { open: true, msg, sev } });

  onAccept = async (o) => {
    try {
      const resp = await this.state.client.pushData(
        `${Config.API_URL_ORDERS}acceptOrder/?iref=${encodeURIComponent(o.iref)}`,
        {}
      );

      if (resp?.status === "Error" || resp?.status === "error") {
        throw new Error(this.extractApiError(resp) || `Error: ${resp.code}`);
      }

      this.toast(`Order #${o.number} accepted`);
      await this.loadOrders();

    } catch (e) {
      this.toast(e.message || "Failed to accept order", "error");
    }
  };

  onCancel = (o) => this.setState({ cancelDlg: { open: true, order: o, reason: "" } });
  
  confirmCancel = async () => {
    const { client, cancelDlg } = this.state;
    try {
      const resp = await client.pushData(
        `${Config.API_URL_ORDERS}cancelOrder/?iref=${encodeURIComponent(cancelDlg.order.iref)}`,
        { reason: cancelDlg.reason }
      );

      if (resp?.status === "Error") {
        throw new Error(resp.message || `Error: ${resp.code}`);
      }

      this.toast(`Order #${cancelDlg.order.number} cancelled`);
      this.setState({ cancelDlg: { open: false, order: null, reason: "" } });
      await this.loadOrders();
    } catch (e) {
      this.toast(this.extractApiError(e) || e.message || "Failed to cancel order", "error");
    }
  };

  onComplete = (o) => {
    try {
      this.state.client.pushData(`${Config.API_URL_ORDERS}completeOrder/?iref=${encodeURIComponent(o.iref)}`, {});
      this.toast(`Order #${o.number} ready`);
      this.loadOrders();
    } catch (e) { this.toast(e.message, "error"); }
  };

  // ----- View switching (single-view page) -----
  openReturnView = () => {
    this.setState({
      viewMode: "returns",
      returnDlg: {
        open: true,          // inline panel visible
        step: 0,
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
        payType: 1,
      }
    });
  };

  backToOrdersView = () => {
    this.setState({
      viewMode: "orders",
      returnDlg: {
        open: false,
        step: 0,
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
      }
    }, () => this.loadOrders()); // optional immediate refresh
  };

  loadReturnSourceByIref = async () => {
    this.setState((s) => ({
      returnDlg: { ...s.returnDlg, busy: true, err: "", success: null },
    }));

    const safeDone = (patch) =>
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, ...(patch || {}) },
      }));

    try {
      const irefTxt = String(this.state.returnDlg.searchIref || "").trim();
      if (!/^\d+$/.test(irefTxt)) {
        throw new Error("Order iref must be numeric.");
      }
      const iref = parseInt(irefTxt, 10);

      const { client, productNameById, comboNameById } = this.state;

      let data = null;
      try {
        data = await Promise.resolve(
          client.getData(Config.API_URL_ORDERS, { iref, status: [1, 2] })
        );
      } catch (_) {}

      let r =
        (data && (data.result || data.order || data.item)) ||
        (data && (data.orders || data.results || data.items)) ||
        data;

      if (Array.isArray(r)) {
        r = r.find((x) => String(x?.iref) === String(iref)) || r[0];
      }
      if (r && (r.result || r.order || r.item)) {
        r = r.result || r.order || r.item;
      }

      if (!r || typeof r !== "object") {
        throw new Error("Order not found or order is not complete.");
      }

      const allLines = [];

      const pushLine = (ln, type, labelPrefix = "") => {
        const product =
          ln?.ProductPrice?.product ??
          ln?.ProductPromotion?.product ??
          ln?.ProductCompany?.product ??
          ln?.product ??
          null;

        const combo =
          ln?.ComboPrice?.productCombo ??
          ln?.ComboPromotion?.productCombo ??
          ln?.ComboCompany?.productCombo ??
          ln?.productCombo ??
          null;

        const parentQty = Math.abs(Number(ln.quantity || 0)) || 1;
        let comboChildren = [];

        if (type === "combo" && Array.isArray(ln.OrderComboProductsEntry_OrderComboEntry)) {
          comboChildren = ln.OrderComboProductsEntry_OrderComboEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        if (
          type === "comboPromo" &&
          Array.isArray(ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry)
        ) {
          comboChildren = ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry.map(
            (ch) => ({
              product: ch.product,
              priceType: ch.priceType,
              priceList: ch.priceList ?? null,
              unitQty: Number(ch.quantity || 0) / parentQty,
              unitVal: Number(ch.value || 0) / parentQty,
              unitVat: Number(ch.vat || 0) / parentQty,
            })
          );
        }

        if (
          type === "comboCompany" &&
          Array.isArray(ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry)
        ) {
          comboChildren = ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        const dictName =
          (product && productNameById?.[String(product)]) ||
          (combo && comboNameById?.[String(combo)]) ||
          null;

        const label =
          labelPrefix +
          (ln.name ||
            ln.productName ||
            ln.comboName ||
            dictName ||
            (product ? `Product ${product}` : combo ? `Combo ${combo}` : "Line"));

        allLines.push({
          key: `${type}_${product || combo}_${allLines.length}`,
          label,
          type,
          product,
          combo,
          productCompany: ln?.ProductCompany?.company ?? null,
          comboCompany: ln?.ComboCompany?.company ?? null,
          qty: Number(ln.quantity || 0),
          value: Number(ln.value || 0),
          discountValue: Number(ln.discountValue || 0),
          vat: Number(ln.vat || 0),
          priceList:
            ln?.ProductPrice?.priceList ??
            ln?.ComboPrice?.priceList ??
            ln?.ProductCompany?.priceList ??
            ln?.ComboCompany?.priceList ??
            null,
          promotionList:
            ln?.ProductPromotion?.promotionList ??
            ln?.ComboPromotion?.promotionList ??
            null,
          comboChildren,
        });
      };

      (r.orderProductEntry_order || []).forEach((l) => pushLine(l, "product", ""));
      (r.orderComboEntry_order || []).forEach((l) => pushLine(l, "combo", ""));
      (r.orderProductPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "prodPromo", "Promo ")
      );
      (r.orderComboPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "comboPromo", "Promo ")
      );
      (r.orderProductCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "productCompany", "")
      );

      (r.orderComboCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "comboCompany", "")
      );

      if (!allLines.length) {
        throw new Error("This order has no lines to return.");
      }

      const sourceOrder = {
        iref: String(r.iref ?? iref),
        code: r.code || "",
        type: String(r.type ?? 0),
        clientIref: String(r?.client?.iref ?? ""),
        pos: r?.pos ?? null,
        date: r.documentDate || "",
        valueWVat: r.totalAmount ?? 0,
        valueWoVat: r.totalAmountWithoutVAT ?? 0,
        discountValue: r.discountValue ?? 0,
        discountValueWithoutVAT: r.discountValueWithoutVAT ?? 0,
      };

      let existing = [];
      try {
        existing = await this.findExistingReturns({
          code: sourceOrder.code,
          iref: sourceOrder.iref,
          date: sourceOrder.date,
        });
      } catch (_) {}

      if (existing.length > 0) {
        safeDone({
          loaded: true,
          step: 0,
          sourceOrder,
          lines: [],
          select: {},
          existingReturns: existing,
          err: "This order already has a return registered.",
        });
        return;
      }

      safeDone({
        loaded: true,
        step: 1,
        sourceOrder,
        lines: allLines,
        select: {},
        existingReturns: [],
        err: "",
      });

      try {
        const existing = await this.findExistingReturns({
          code: r.code || "",
          iref: String(r.iref ?? iref),
          date: r.documentDate || "",
        });

        this.setState((s) => ({
          returnDlg: { ...s.returnDlg, existingReturns: existing },
        }));
      } catch (_) {}
    } catch (e) {
      safeDone({ err: e?.message || "Load failed." });
    }
  };

  findExistingReturns = async (sourceOrder) => {
    const { client } = this.state;
    const ref = sourceOrder?.code || "";
    if (!ref) return [];

    let date = sourceOrder?.date || null;
    if (date) {
      date = String(date).trim();
      if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
        const [dd, mm, yyyy] = date.split("-");
        date = `${yyyy}-${mm}-${dd}`;
      } else if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        date = date.slice(0, 10);
      } else {
        const d = new Date(date);
        date = isNaN(d) ? null : d.toISOString().slice(0, 10);
      }
    }

    const q = {
      documentDate__gte: date,
      description: `Return for order ${ref}`,
      // limit: 1,
    };

    try {
      const resp = await Promise.resolve(client.getData(Config.API_URL_ORDERS, q));
      const list = Array.isArray(resp) ? resp : (resp.orders || []);
      // Filter out the original order if it ever matched by mistake
      return list.filter(o => String(o.description || "").includes(`Return for order ${ref}`));
    } catch {
      return [];
    }
  };

  extractApiError = (resOrErr) => {
    try {
      const r = resOrErr?.response || resOrErr; // in case some client wraps it
      if (r?.status === "Error" || r?.status === "error") {
        const parts = [];
        if (r.code) parts.push(String(r.code));
        if (r.message) parts.push(String(r.message));
        const d = r.data;
        const dataMsg =
          (d && (d.message || d.detail || d.error || d.reason)) || null;
        if (dataMsg) parts.push(String(dataMsg));
        return parts.join(" — ");
      }
      // If it's a thrown Error
      if (r instanceof Error && r.message) return r.message;
      // Some backends put errors directly in data.*
      const d = r?.data;
      if (d && (d.message || d.detail || d.error)) {
        return String(d.message || d.detail || d.error);
      }
    } catch (_) {}
    return null;
  };

  openDetails = (order) => {
    // Debug: check missing ids vs dictionaries
    try {
      const o = order?.raw || order;
      const prodMap = this.state.productNameById || {};
      const comboMap = this.state.comboNameById || {};
      const needProd = new Set();
      const needCombo = new Set();
      (o?.orderProductEntry_order || []).forEach(p => {
        const pid = String(p?.ProductPrice?.product ?? p?.product ?? "");
        if (pid && !prodMap[pid]) needProd.add(pid);
      });
      (o?.orderComboEntry_order || []).forEach(c => {
        const cid = String(c?.ComboPrice?.productCombo ?? c?.productCombo ?? "");
        if (cid && !comboMap[cid]) needCombo.add(cid);
      });
      (o?.orderProductPromotionEntry_order || []).forEach(pp => {
        const pid = String(pp?.ProductPromotion?.product ?? pp?.product ?? "");
        if (pid && !prodMap[pid]) needProd.add(pid);
      });
      (o?.orderComboPromotionEntry_order || []).forEach(cp => {
        const cid = String(cp?.ComboPromotion?.productCombo ?? cp?.productCombo ?? "");
        if (cid && !comboMap[cid]) needCombo.add(cid);
      });
      if (needProd.size || needCombo.size) {
        console.warn("[Details] Missing in catalogs -> products:", Array.from(needProd), "combos:", Array.from(needCombo));
      }
    } catch(e) {}
    this.setState({ detailsDlg: { open: true, order } });
  };
  closeDetails = () => this.setState({ detailsDlg: { open: false, order: null } });

  renderProductsCell = (o) => (
    <Stack direction="row" flexWrap="wrap">
      {o.items.map((it) => (
        <Chip key={it.key} size="small" label={it.label} color={it.color} icon={<ShoppingBag fontSize="inherit" />} sx={{ mr: 0.5, mb: 0.5 }} />
      ))}
    </Stack>
  );

  fillReturnAll = () => {
    this.setState((s) => {
      const sel = {};
      (s.returnDlg.lines || []).forEach((ln) => {
        sel[ln.key] = Math.max(0, Number(ln.qty || 0));
      });
      return { returnDlg: { ...s.returnDlg, select: sel } };
    });
  };
  clearReturnSelection = () => {
    this.setState((s) => ({ returnDlg: { ...s.returnDlg, select: {} } }));
  };

  buildReturnPayload = () => {
    const { returnDlg } = this.state;
    const { sourceOrder, lines, select, payType } = returnDlg;

    const chosen = lines
      .map((ln) => {
        const q = Number(select[ln.key] || 0);
        return q > 0 ? { ...ln, retQty: q } : null;
      })
      .filter(Boolean);

    const orderProductEntry_order = [];
    const orderComboEntry_order = [];
    const orderProductCompanyEntry_order = [];
    const orderComboCompanyEntry_order = [];
    const orderProductPromotionEntry_order = [];
    const orderComboPromotionEntry_order = [];

    const isFullReturn = chosen.length === 0;

    if (isFullReturn) {
      return {
        orderIref: Number(sourceOrder?.iref),
        payType: Number(payType || 1),
        createdOn: new Date().toISOString(),
      };
    }

    chosen.forEach((ln) => {
      const origQty = Math.abs(Number(ln.qty || 0)) || 1;
      const unitVal = Number(ln.value || 0) / origQty;
      const unitVat = Number(ln.vat || 0) / origQty;

      const max = Math.max(0, Number(ln.qty || 0));
      const retQty = Math.min(Math.abs(Number(ln.retQty)), max);

      const q = retQty;
      const lineVal = Math.round(unitVal * q * 1000000) / 1000000;
      const lineVat = Math.round(unitVat * q * 1000000) / 1000000;

      const common = { value: lineVal, quantity: q, vat: lineVat };
      const usePromo = !!ln.promotionList;

      if (ln.product && !ln.combo) {
        if (usePromo) {
          orderProductPromotionEntry_order.push({
            ...common,
            ProductPromotion: {
              promotionList: ln.promotionList,
              product: ln.product,
            },
          });
        } else if (ln.type === "productCompany") {
          orderProductCompanyEntry_order.push({
            ...common,
            ProductCompany: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        } else {
          orderProductEntry_order.push({
            ...common,
            ProductPrice: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        }
      } else if (ln.combo) {
        if (usePromo) {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboPromotionEntry_order.push({
            ...common,
            ComboPromotion: {
              productCombo: ln.combo,
              promotionList: ln.promotionList,
            },
            OrderComboPromotionProductsEntry_OrderComboPromotionEntry: children,
          });
        } else if (ln.type === "comboCompany") {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboCompanyEntry_order.push({
            ...common,
            ComboCompany: {
              priceList: ln.priceList,
              productCombo: ln.combo,
            },
            OrderComboCompanyProductsEntry_OrderComboCompanyEntry: children,
          });
        } else {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboEntry_order.push({
            ...common,
            ComboPrice: {
              productCombo: ln.combo,
              priceList: ln.priceList,
            },
            OrderComboProductsEntry_OrderComboEntry: children,
          });
        }
      }
    });

    return {
      orderIref: Number(sourceOrder?.iref),
      payType: Number(payType || 1),
      createdOn: new Date().toISOString(),
      orderProductEntry_order,
      orderComboEntry_order,
      orderProductCompanyEntry_order,
      orderComboCompanyEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };
  };

  submitReturn = async () => {
    if (this.state.returnDlg.existingReturns?.length > 0) {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, err: "A return already exists for this order." },
      }));
      return;
    }

    try {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: true, err: "" },
      }));

      const payload = this.buildReturnPayload();
      const url = `${Config.API_URL_ORDERS}returnOrder/`;

      const res = this.state.client.pushJson
        ? await this.state.client.pushJson(url, payload)
        : await this.state.client.pushData(url, payload);

      if (res?.status === "Error" || res?.status === "error") {
        const msg = this.extractApiError(res) || `Error: ${res?.code || "RETURN-FAIL"}`;
        throw new Error(msg);
      }

      this.toast(res?.message || "Return order created");

      this.setState((s) => ({
        returnDlg: {
          ...s.returnDlg,
          busy: false,
          err: "",
          step: 2,
          success: res,
        },
      }));

      if (this.props.onSuccess) this.props.onSuccess(res);
    } catch (e) {
      const msg = this.extractApiError(e) || e?.message || "Submit failed.";
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, err: msg },
      }));
    }
  };

  componentDidMount() {
    this.bootstrap();
  }

  bootstrap = async () => {
    try {
      this.setState({ loading: true, error: "" });

      // 1) Fetch dictionaries and build { iref -> name } maps
      await this.loadDictionaries();

      // 2) Fetch orders
      await this.loadOrders();

      // 3) Start auto-refresh — but only while in Orders view
      this.timer = setInterval(() => {
        if (this.state.viewMode === "orders") {
          this.loadOrders();
        }
      }, AUTO_REFRESH_MS);
    } catch (e) {
      this.setState({ error: e?.message || "Init failed" });
    } finally {
      this.setState({ loading: false });
    }
  };

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  // ----------------- Render -----------------
  render() {
    const { orders, loading, error, snack, cancelDlg, selectedStatus, selectedDate, detailsDlg } = this.state;
    const countsByStatus = orders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {});

    if (!this.state.catalogsLoaded) {
      return (
        <Box sx={{ minHeight: "80v  h", maxHeight: "90vh", display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          height: "90vh", bgcolor: "background.default",
          width: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          ml: {
            xs: 'calc(50% - 50vw + 4px)',
            sm: 0,
          },   
        }}
      >

        <Container
          maxWidth="xl"
          sx={{
            py: 3,
            maxHeight: 'calc(90vh - 64px)',
            // overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              p: 2,
              position: "sticky",
              top: (t) => t.mixins.toolbar?.minHeight ?? 0,
              zIndex: (t) => t.zIndex.appBar + 1,
              bgcolor: "background.paper"
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
              rowGap={1}
            >
              {/* LEFT: status buttons + date (only in Orders view) */}
              {this.state.viewMode === "orders" && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant={selectedStatus === 5 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => this.handleStatusClick(5)}
                  >
                    Paid ({countsByStatus[5] || ""})
                  </Button>
                  <Button
                    variant={selectedStatus === 6 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => this.handleStatusClick(6)}
                  >
                    Accepted ({countsByStatus[6] || ""})
                  </Button>

                  <TextField
                    type="date"
                    label="Promised date"
                    size="small"
                    value={selectedDate}
                    onChange={this.handleDateChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ml: 1 }}
                  />
                </Stack>
              )}

              {/* RIGHT: title + refresh or toggle */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" noWrap>
                  {this.state.viewMode === "orders" ? "Orders Dashboard" : "Register return"}
                </Typography>

                {this.state.viewMode === "orders" ? (
                  <>
                    <IconButton onClick={() => this.loadOrders()}>
                      <Refresh />
                    </IconButton>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={this.openReturnView}
                    >
                      Register return
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={this.backToOrdersView}
                  >
                    Back to orders
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {/* ===== Views ===== */}
          {this.state.viewMode === "orders" ? (
            // ===== ORDERS TABLE VIEW =====
            <Paper variant="outlined">
              <Box sx={{ p: 2 }}>
                {loading ? (
                  <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : (
                  <TableContainer sx={{ p: 2, maxHeight: 'calc(75vh - 64px)', overflow: 'auto' }} >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Products</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Items</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.length === 0 ? (
                          <TableRow><TableCell colSpan={9} align="center">No orders</TableCell></TableRow>
                        ) : (
                          orders.map((o) => (
                            <TableRow key={o.iref} hover>
                              <TableCell>{o.locationName}</TableCell>
                              <TableCell>{o.created}</TableCell>
                              <TableCell>
                                <Typography variant="subtitle2">#{o.number}</Typography>
                              </TableCell>
                              <TableCell>{o.customerName}</TableCell>
                              <TableCell>{this.renderProductsCell(o)}</TableCell>
                              <TableCell align="right">{o.itemsCount}</TableCell>
                              <TableCell align="right">{Number(o.total || 0).toFixed(2)}</TableCell>
                              <TableCell><StatusChip status={o.status} /></TableCell>
                              <TableCell align="right">
                                <Actions
                                  row={o}
                                  onAccept={this.onAccept}
                                  onCancel={this.onCancel}
                                  onComplete={this.onComplete}
                                  onDetails={this.openDetails}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
          ) : (
            // ===== RETURN VIEW =====
            <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  maxHeight: 'calc(92vh - 40px)',
                  overflow: 'hidden'
                }}
              >
              {/* Step 0: Find Order by iref */}
              {this.state.returnDlg.step === 0 && (
                <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
                  {this.state.returnDlg.err ? (
                    <Alert severity="error">{this.state.returnDlg.err}</Alert>
                  ) : null}

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      label="Order iref"
                      size="small"
                      value={this.state.returnDlg.searchIref}
                      onChange={(e)=>this.setState(s=>({
                        returnDlg:{ ...s.returnDlg, searchIref: e.target.value.replace(/\D/g,"") }
                      }))}
                      onKeyDown={(e)=>{ if(e.key==='Enter'){ this.loadReturnSourceByIref(); } }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      disabled={this.state.returnDlg.busy}
                    />
                    <Button
                      variant="contained"
                      onClick={this.loadReturnSourceByIref}
                      disabled={!this.state.returnDlg.searchIref || this.state.returnDlg.busy}
                    >
                      {this.state.returnDlg.busy ? "Loading..." : "Load order"}
                    </Button>
                    <Button size="small" onClick={this.backToOrdersView}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* Step 1: Select Lines to return */}
              {Array.isArray(this.state.returnDlg.existingReturns) && this.state.returnDlg.existingReturns.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  This order already has {this.state.returnDlg.existingReturns.length} return(s).
                  {this.state.returnDlg.existingReturns.slice(0,3).map((o, i) => (
                    <span key={i}> #{o.docNumber || o.iref} — {o.createdOn} — total {Number(o.totalAmount || 0).toFixed(2)};&nbsp;</span>
                  ))}
                </Alert>
              )}
              {this.state.returnDlg.step === 1 && (
                <Stack spacing={1.5}>
                  {this.state.returnDlg.err ? (
                    <Alert severity="error">{this.state.returnDlg.err}</Alert>
                  ) : null}

                  <Stack
                    sx={{
                      px: 1,
                      py: 0.75,
                      bgcolor: (t) => t.palette.action.hover,
                      borderRadius: 1,
                    }}
                    spacing={0.5}
                  >
                    {/* First row */}
                    <Stack
                      direction="row"
                      spacing={1}
                      divider={<Divider orientation="vertical" flexItem />}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {[
                        {
                          label: "Order",
                          value: this.state.returnDlg.sourceOrder?.code
                            ? `${this.state.returnDlg.sourceOrder.code}${
                                this.state.returnDlg.sourceOrder?.iref
                                  ? ` (${this.state.returnDlg.sourceOrder.iref})`
                                  : ""
                              }`
                            : "-",
                        },
                        { label: "POS", value: this.state.returnDlg.sourceOrder?.pos?.name || "-" },
                        { label: "Date", value: this.state.returnDlg.sourceOrder?.date || "-" },
                      ].map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {item.label}:
                          </Typography>
                          <Typography variant="body2">{item.value}</Typography>
                        </Box>
                      ))}
                    </Stack>

                    {/* Second row */}
                    <Stack
                      direction="row"
                      spacing={1}
                      divider={<Divider orientation="vertical" flexItem />}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {[
                        {
                          label: "Total",
                          value: `${Number(this.state.returnDlg.sourceOrder?.valueWVat || 0).toFixed(2)}`,
                        },
                        {
                          label: "Discount included",
                          value: this.state.returnDlg.sourceOrder?.discountValue
                            ? `${Number(this.state.returnDlg.sourceOrder.discountValue).toFixed(2)}`
                            : "0.00",
                        },
                        {
                          label: "Lines",
                          value: this.state.returnDlg.lines.length,
                        },
                      ].map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {item.label}:
                          </Typography>
                          <Typography variant="body2">{item.value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Select products to return
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={this.fillReturnAll}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                      >
                        Return all
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={this.clearReturnSelection}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Stack>

                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Line</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Qty (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Line value (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Discount (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Return qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.returnDlg.lines.map(ln => {
                            const orig = Number(ln.qty || 0);
                            const max = Math.max(0, orig); // cannot return more than positive qty
                            const cur = Number(this.state.returnDlg.select[ln.key] || 0);
                            return (
                              <TableRow key={ln.key}>
                                <TableCell>{ln.label}</TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ color: orig < 0 ? 'error.main' : 'text.primary', fontWeight: 500 }}
                                  >
                                    {orig}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{Number(ln.value || 0).toFixed(2)}</TableCell>
                                <TableCell align="right">{Number(ln.discountValue || 0).toFixed(2)}</TableCell>
                                <TableCell align="right" width={180}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, max }}
                                    value={cur}
                                    onChange={(e)=>{
                                      const v = Math.max(0, Math.min(max, Number(e.target.value || 0)));
                                      this.setState(s=>({
                                        returnDlg:{ ...s.returnDlg, select: { ...s.returnDlg.select, [ln.key]: v } }
                                      }));
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Live totals preview */}
                  {(() => {
                    const { lines, select } = this.state.returnDlg;
                    let subGross = 0, subVat = 0, discGross = 0, discNet = 0;
                    lines.forEach(ln => {
                      const origQty = Math.abs(Number(ln.qty || 0)) || 1;
                      const unitVal = Number(ln.value || 0) / origQty; // gross/unit
                      const unitVat = Number(ln.vat || 0) / origQty;   // vat/unit
                      const q = -Math.abs(Number(select[ln.key] || 0)); // negative for return
                      subGross += unitVal * q;
                      subVat   += unitVat * q;
                      // discount accumulators (proportional to returned qty)
                      const unitDiscGross = Number(ln.discountValue || 0) / origQty;
                      if (unitDiscGross) {
                        const vatShareInGross = unitVal ? (unitVat / unitVal) : 0;
                        const unitDiscNet = unitDiscGross * (1 - vatShareInGross);
                        const retQty = Math.abs(Number(select[ln.key] || 0));
                        discGross += retQty * unitDiscGross;
                        discNet   += retQty * unitDiscNet;
                      }
                    });
                    const subNet = subGross - subVat;
                    const totalGross = subGross + discGross;
                    const totalNet   = subNet   + discNet;
                    return (
                      <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2">
                          Subtotal (gross): <b>{subGross.toFixed(2)}</b> · Discount (gross): <b>{-discGross.toFixed(2)}</b> · Total (gross): <b>{totalGross.toFixed(2)}</b>
                        </Typography>
                        <Typography variant="body2">
                          Subtotal (net): <b>{subNet.toFixed(2)}</b> · Discount (net): <b>{-discNet.toFixed(2)}</b> · Total (net): <b>{totalNet.toFixed(2)}</b> · Payment: <b>{this.state.returnDlg.payType} ({this.state.returnDlg.payType === 1 ? "Cash" : "Bank Transfer"})</b> · Amount: <b>{totalGross.toFixed(2)}</b>
                        </Typography>
                      </Alert>
                    );
                  })()}

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      mt: 1,
                      position: "sticky",
                      bottom: 0,
                      bgcolor: "background.paper",
                      pt: 1,
                      borderTop: (t) => `1px solid ${t.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() =>
                        this.setState((s) => ({ returnDlg: { ...s.returnDlg, step: 0 } }))
                      }
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      ← Back
                    </Button>

                    <TextField
                      select
                      label="Payment"
                      size="small"
                      value={this.state.returnDlg.payType}
                      onChange={(e) =>
                        this.setState((s) => ({
                          returnDlg: { ...s.returnDlg, payType: Number(e.target.value) }
                        }))
                      }
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 180 }}
                    >
                      <option value={1}>Cash</option>
                      <option value={2}>Bank Transfer</option>
                    </TextField>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={this.submitReturn}
                      disabled={this.state.returnDlg.busy || (this.state.returnDlg.existingReturns?.length > 0)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        minWidth: 140,
                      }}
                    >
                      {this.state.returnDlg.busy ? "Submitting…" : "Create return"}
                    </Button>

                    <Button
                      variant="text"
                      color="inherit"
                      onClick={this.backToOrdersView}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Close
                    </Button>
                  </Stack>

                </Stack>
              )}
            </Paper>
          )}

          {/* ===== Details dialog (unchanged) ===== */}
          <Dialog open={detailsDlg.open} onClose={this.closeDetails} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pb: 1.5 }}>
              <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={1}>
                <Typography variant="h6" component="span" sx={{ mr: 0.5 }}>
                  Order details #{detailsDlg.order?.number || detailsDlg.order?.iref}
                </Typography>
                {detailsDlg.order?.raw?.pos?.name && (
                  <Typography variant="subtitle2" color="text.secondary" component="span">
                    — {detailsDlg.order.raw.pos.name}
                  </Typography>
                )}
              </Box>

              {(detailsDlg.order?.raw?.createdOn ||
                detailsDlg.order?.raw?.scheduledOn ||
                detailsDlg.order?.raw?.totalAmount) && (
                <Stack
                  direction="row"
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                  sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
                >
                  {detailsDlg.order?.raw?.createdOn && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Created:</Box>{' '}
                      {detailsDlg.order.raw.createdOn}
                    </Typography>
                  )}
                  {detailsDlg.order?.raw?.scheduledOn && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Scheduled:</Box>{' '}
                      {detailsDlg.order.raw.scheduledOn}
                    </Typography>
                  )}
                  {detailsDlg.order?.raw?.totalAmount && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Total:</Box>{' '}
                      {Number(detailsDlg.order.raw.totalAmount || 0).toFixed(2)}
                    </Typography>
                  )}
                </Stack>
              )}

              {detailsDlg.order?.raw?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Notes:</Box>{' '}
                  {detailsDlg.order.raw.description}
                </Typography>
              )}
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                maxHeight: '70vh',
                overflowY: 'auto',
                pt: 2
              }}
            >
              {(() => {
                const o = detailsDlg.order?.raw;
                if (!o) return <Typography variant="body2">No data</Typography>;

                const prods = o.orderProductEntry_order || [];
                const combos = o.orderComboEntry_order || [];
                const pPromos = o.orderProductPromotionEntry_order || [];
                const cPromos = o.orderComboPromotionEntry_order || [];

                const { delivery, address } = this.getDeliveryInfo(o);

                const net = Number(o.totalAmount || 0);
                const discount = Number(o.discountValue || 0);
                const total = net + discount;

                return (
                  <Stack spacing={2}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "background.paper",
                        width: "100%",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          flexWrap="wrap"
                        >
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              Delivery details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {this.getDeliveryTypeLabel(o?.deliveryType)}
                              {delivery?.expectedDelivery ? ` · Expected: ${delivery.expectedDelivery}` : ""}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={`Pickup code: ${this.getPickupCode(o, delivery)}`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Stack>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                              md: "1.2fr 1fr 1fr",
                            },
                            gap: 1.25,
                          }}
                        >
                          <this.InfoLine
                            label="Client"
                            value={this.getClientName(o)}
                          />

                          <this.InfoLine
                            label="Phone"
                            value={delivery?.clientPhone || this.getClientPhone(o)}
                          />

                          <this.InfoLine
                            label="Status"
                            value={delivery?.status ?? o?.deliveryStatus}
                          />
                        </Box>

                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: (t) => t.palette.action.hover,
                            border: (t) => `1px solid ${t.palette.divider}`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}
                          >
                            Delivery address
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.25,
                              fontWeight: 500,
                              lineHeight: 1.5,
                              wordBreak: "break-word",
                            }}
                          >
                            {this.formatAddress(address)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                              md: "1fr 1fr 1fr",
                            },
                            gap: 1.25,
                          }}
                        >
                          <this.InfoLine
                            label="Delivery type"
                            value={this.getDeliveryTypeLabel(o?.deliveryType)}
                          />

                          <this.InfoLine
                            label="Expected delivery"
                            value={delivery?.expectedDelivery}
                          />

                          <this.InfoLine
                            label="Pickup code"
                            value={this.getPickupCode(o, delivery)}
                          />

                          <this.InfoLine
                            label="Tracking number"
                            value={delivery?.trackingNumber}
                          />

                          <this.InfoLine
                            label="Delivery fee"
                            value={`${Number(delivery?.fee || 0).toFixed(2)}`}
                          />

                          <this.InfoLine
                            label="Status"
                            value={delivery?.status ?? o?.deliveryStatus}
                          />
                        </Box>

                        {delivery?.details && (
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 1.25,
                              bgcolor: "background.default",
                              border: (t) => `1px dashed ${t.palette.divider}`,
                            }}
                          >
                            <this.InfoLine
                              label="Delivery notes"
                              value={delivery.details}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                    <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '55%' }}>Item</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">Qty</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">Value</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">VAT</TableCell>
                          </TableRow>
                        </TableHead>
                      </Table>
                    </TableContainer>

                    {prods.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Products
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {prods.map((p, idx) => (
                                <TableRow
                                  key={`p_${p.iref ?? this.getProductIref(p)}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const pid = this.getProductIref(p);
                                      return (this.state.productNameById || {})[pid] || pid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {p.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(p.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(p.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {combos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Combos
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {combos.map((c, idx) => (
                                <TableRow
                                  key={`c_${c.iref ?? this.getComboIref(c)}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const cid = this.getComboIref(c);
                                      return (this.state.comboNameById || {})[cid] || cid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {c.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(c.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(c.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {pPromos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Product promotions
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {pPromos.map((pp, idx) => (
                                <TableRow
                                  key={`pp_${pp.iref ?? (pp?.ProductPromotion?.product ?? '')}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const pid = String(pp?.ProductPromotion?.product ?? pp?.product ?? '');
                                      return (this.state.productNameById || {})[pid] || pid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {pp.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(pp.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(pp.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {cPromos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Combo promotions
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {cPromos.map((cp, idx) => (
                                <TableRow
                                  key={`cp_${cp.iref ?? (cp?.ComboPromotion?.productCombo ?? '')}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const cid = String(cp?.ComboPromotion?.productCombo ?? cp?.productCombo ?? '');
                                      return (this.state.comboNameById || {})[cid] || cid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {cp.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(cp.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(cp.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {(total || discount) ? (
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        sx={{ mt: 1 }}
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 260 }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Subtotal (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {total.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Discount (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              − {discount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Total (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {net.toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>
                );
              })()}
            </DialogContent>

            <DialogActions>
              <Button onClick={this.closeDetails} variant="contained">Close</Button>
            </DialogActions>
          </Dialog>

          {/* Cancel dialog */}
          <Dialog open={cancelDlg.open} onClose={() => this.setState({ cancelDlg: { open: false, order: null, reason: "" } })}>
            <DialogTitle>Cancel order</DialogTitle>
            <DialogContent>
              <DialogContentText>Reason for cancelling order #{cancelDlg.order?.number}</DialogContentText>
              <TextField fullWidth value={cancelDlg.reason} onChange={(e) => this.setState({ cancelDlg: { ...cancelDlg, reason: e.target.value } })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ cancelDlg: { open: false, order: null, reason: "" } })}>Close</Button>
              <Button color="error" variant="contained" onClick={this.confirmCancel} startIcon={<Cancel />}>Cancel order</Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => this.setState({ snack: { open: false, msg: "", sev: snack.sev } })} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
            <Alert onClose={() => this.setState({ snack: { open: false, msg: "", sev: snack.sev } })} severity={snack.sev} sx={{ width: "100%" }}>{snack.msg}</Alert>
          </Snackbar>
        </Container>
      </Box>
    );
  }
}